import {tva} from "@gluestack-ui/nativewind-utils/tva";
import {format} from "date-fns";
import type {ComponentProps} from "react";
import React, {memo, useCallback, useMemo} from "react";

import {useView} from "../../hooks/useView";
import {buildChannel} from "../../models/channel";
import type {ListingChannel, SelectChannelType} from "../../models/listing";
import {CalendarIcon, CalendarMultipleIcon, CheckedIcon, ChevronDownIcon} from "../../react";
import MonthNavigator from "../MonthNavigator";
import {TabGroup, TabItem} from "../Tabs";
import {Box} from "../ui/box";
import {Button, ButtonIcon, ButtonText} from "../ui/button";
import {HStack} from "../ui/hstack";
import {Menu, MenuItem, MenuItemLabel} from "../ui/menu";

import ChannelTabItem from "./ChannelTabItem";
import SelectMonths from "./SelectMonths";

const style = tva({
    base: "flex-wrap justify-between items-center shrink-0 grow-0 z-10",
    variants: {
        isMobile: {
            true: "flex-nowrap"
        },
        hasOneElement: {
            true: "flex-end"
        }
    }
});

export type Props = {
    channel: SelectChannelType;
    channels: ListingChannel[];
    currentDate: Date;
    className?: string;
    onToday: () => void;
    onChangeChannel: (channel: SelectChannelType) => void;
    onMovePrev: () => void;
    onMoveNext: () => void;
} & StrictUnion<
    | {
          onChangeMonth: (date: Date) => void;
      }
    | {
          isNative: true;
          viewMode: string;
          onViewModeChange: (value: string) => void;
          onOpenMonthModal: () => void;
      }
>;

const CalendarNavHeader: React.FC<Props> = ({
    isNative,
    channel,
    channels,
    currentDate,
    className,
    viewMode,
    onViewModeChange,
    onToday,
    onChangeChannel,
    onChangeMonth,
    onMovePrev,
    onMoveNext,
    onOpenMonthModal
}) => {
    const view = useView();
    const isMobile = view === "native" || view === "webMobile";
    const [selectedChannel, setSelectedChannel] = React.useState(new Set([channel]));

    const triggerBuilder = useCallback(
        (props: ComponentProps<typeof Button>) => {
            const option = channel !== "all" ? buildChannel(channel) : undefined;
            return (
                // `shrink` will override the `shrink-0` which is the default style
                // to allow the text to be truncated
                <Button no110MinWidth className="shrink" size="sm" variant="outline" {...props}>
                    <ButtonText numberOfLines={1}>{option ? option.label : "All"}</ButtonText>
                    <ButtonIcon as={ChevronDownIcon} fill="primary.600" />
                </Button>
            );
        },
        [channel]
    );

    const handleMenuChange = useCallback<
        NonNullable<ComponentProps<typeof Menu>["onSelectionChange"]>
    >(
        value => {
            // the team has created a bad type for the argument
            if (typeof value !== "string" && "currentKey" in value) {
                setSelectedChannel(value as Set<SelectChannelType>);
                onChangeChannel(value.currentKey as SelectChannelType);
            }
        },
        [onChangeChannel]
    );

    const channelItems = useMemo(() => {
        // `all` channel is auto added when there are at least 2 channels
        const displayChannels = channels.length > 1 ? (["all", ...channels] as const) : channels;
        return displayChannels.map((elem, idx, original) => {
            if (process.env.IS_WEB && !isMobile) {
                return (
                    <ChannelTabItem
                        key={elem}
                        active={channel === elem}
                        channel={elem}
                        testID={`btn-channel-${elem}`}
                        onChangeChannel={onChangeChannel}
                    />
                );
            }
            const option = elem !== "all" ? buildChannel(elem) : undefined;

            // the `Menu` appears to look for the children as `MenuItem` which means
            // it doesn't work to split this as child component
            return (
                <MenuItem
                    key={elem}
                    // `last` prefix doesn't work on native so we have to manually remove the border for the last item
                    className={`${idx === original.length - 1 ? "border-b-0" : ""} justify-between`}
                    textValue={elem}
                >
                    <HStack space="sm">
                        {option && elem !== "all" && (
                            <option.LogoIcon data-testid={channel} fill={elem} size="sm" />
                        )}
                        <MenuItemLabel size="sm">{option ? option.label : "All"}</MenuItemLabel>
                    </HStack>
                    {channel === elem && <CheckedIcon fill="blueGray.600" size="sm" />}
                </MenuItem>
            );
        });
    }, [isMobile, channel, channels, onChangeChannel]);

    return (
        <HStack
            className={style({
                isMobile: isMobile || isNative,
                hasOneElement: channelItems.length <= 1,
                className
            })}
            space="xs"
        >
            {channelItems.length > 1 &&
                (isMobile || isNative ? (
                    <Menu
                        placement="bottom"
                        selectedKeys={selectedChannel}
                        selectionMode="single"
                        trigger={triggerBuilder}
                        onSelectionChange={handleMenuChange}
                    >
                        {channelItems}
                    </Menu>
                ) : (
                    <TabGroup activeTab="all" className="shrink-0 grow-0" testID="channel-selector">
                        {channelItems}
                    </TabGroup>
                ))}

            {!process.env.IS_WEB && isNative && (
                <Box className="grow-0 shrink-0">
                    <TabGroup activeTab={viewMode} size="sm" onTabChange={onViewModeChange}>
                        <TabItem Icon={CalendarMultipleIcon} id="multi" testID="view-mode-multi" />
                        <TabItem Icon={CalendarIcon} id="single" testID="view-mode-single" />
                    </TabGroup>
                </Box>
            )}

            <HStack
                // somehow `min-w-0` makes elment not to scale with `flex-1`
                className="items-center justify-end flex-1 min-w-[auto]"
                space={isMobile ? "sm" : "lg"}
            >
                <MonthNavigator onMoveNext={onMoveNext} onMovePrev={onMovePrev} onToday={onToday} />
                {process.env.IS_WEB && !isNative ? (
                    <SelectMonths currentDate={currentDate} onSelectMonth={onChangeMonth} />
                ) : (
                    <Button
                        no110MinWidth
                        action="default"
                        variant="ghost"
                        onPress={onOpenMonthModal}
                    >
                        <ButtonText testID="current-month-year">
                            {`${format(currentDate, "LLL")} ${currentDate.getFullYear()}`}
                        </ButtonText>
                        <ButtonIcon as={ChevronDownIcon} />
                    </Button>
                )}
            </HStack>
        </HStack>
    );
};

export default memo(CalendarNavHeader);
